package br.com.vvrefeicoes.portal.feature.orgchart

import br.com.vvrefeicoes.portal.core.util.PdfCanvasDocument
import br.com.vvrefeicoes.portal.core.util.PdfCanvasPage
import br.com.vvrefeicoes.portal.core.util.PdfColor
import br.com.vvrefeicoes.portal.core.util.PdfImage
import br.com.vvrefeicoes.portal.core.util.PdfOp
import br.com.vvrefeicoes.portal.core.util.PdfPageSize
import br.com.vvrefeicoes.portal.core.util.PdfText
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.CompanyType
import br.com.vvrefeicoes.portal.domain.model.Director
import br.com.vvrefeicoes.portal.domain.model.Manager
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.OrgChartVersion
import br.com.vvrefeicoes.portal.domain.model.companiesOfManager
import br.com.vvrefeicoes.portal.domain.model.directCompaniesOfDirector
import br.com.vvrefeicoes.portal.domain.model.managersOfDirector
import br.com.vvrefeicoes.portal.domain.model.managersWithoutDirector
import br.com.vvrefeicoes.portal.domain.model.orgChartSortKey

data class OrgChartPdfLabels(
    val title: String,
    val slogan: String,
    val totals: Map<CompanyType, String>,
    val legend: Map<CompanyType, String>,
    val rights: String,
)

private const val PAGE_MARGIN_X = 36f
private const val HEADER_LOGO_TOP = 24f
private const val TITLE_TOP = 28f
private const val SLOGAN_TOP = 52f
private const val TOTALS_TOP = 62f
private const val CONTENT_TOP = 106f
private const val CHART_BOTTOM = 451f
private const val LEGEND_BOTTOM = 525f
private const val FOOTER_LOGO_TOP = 548f
private const val FOOTER_TEXT_TOP = 551f
private const val FOOTER_RIGHTS_TOP = 564f

private const val BLOCK_HEIGHT = 40f
private const val SPINE_GAP = 14f
private const val BUS_DROP = 8f
private const val MANAGER_HEIGHT = 19f
private const val UNIT_HEIGHT = 18f
private const val CONNECTOR = 8f
private const val UNIT_GAP = 6f
private const val UNIT_HORIZONTAL_GAP = 8f
private const val COLUMN_GAP = 18f
private const val ROW_GAP = 14f
private const val SHARED_MANAGER_GAP = 8f
private const val CONSOLIDATED_TOP = 485f
private const val TOTAL_CELL_WIDTH = 44f
private const val TOTAL_CELL_HEIGHT = 19f
private const val TOTAL_CELL_GAP = 2f

private const val MANAGER_FONT = 6.5f
private const val UNIT_FONT = 6.5f
private const val BLOCK_FONT = 12f

private val TEXT_COLOR = PdfColor(33, 37, 41)
private val MUTED_COLOR = PdfColor(108, 117, 125)
private val SURFACE_COLOR = PdfColor(255, 255, 255)
private val CONNECTOR_COLOR = PdfColor(73, 80, 87)
private val PRIMARY_COLOR = PdfColor(0, 95, 115)
private val TOTAL_HEADER_FILL = PdfColor(214, 226, 240)
private val TOTAL_VALUE_FILL = PdfColor(234, 240, 247)
private val LEGEND_FILL = PdfColor(191, 227, 238)

private data class ManagerBlock(
    val manager: Manager,
    val companies: List<Company>,
)

private data class DirectorPage(
    val director: Director?,
    val managers: List<ManagerBlock>,
    val directCompanies: List<Company>,
)

private data class ColumnBlock(
    val managers: List<Manager>,
    val companies: List<Company>,
)

private data class ColumnLayout(
    val block: ColumnBlock,
    val width: Float,
    val height: Float,
)

private data class RowLayout(
    val columns: List<ColumnLayout>,
    val width: Float,
    val height: Float,
)

private data class UnitLine(
    val companies: List<Company>,
    val width: Float,
)

private data class PlacedUnit(
    val company: Company,
    val x: Float,
    val top: Float,
    val width: Float,
)

private data class PillStyle(
    val fill: PdfColor,
    val border: PdfColor?,
    val text: PdfColor,
)

private val MANAGER_STYLE = PillStyle(PdfColor(222, 226, 230), null, TEXT_COLOR)
private val PRIMARY_MANAGER_STYLE = PillStyle(PRIMARY_COLOR, null, SURFACE_COLOR)

private enum class Align { Left, Center, Right }

/**
 * Gera o organograma em PDF (A4 paisagem) com uma página por diretoria e uma
 * última página para gestoras/unidades sem diretoria. O layout é reduzido de
 * escala quando necessário e as gestoras quebram em mais de uma linha.
 */
fun buildOrgChartPdf(
    data: OrgChartData,
    version: OrgChartVersion?,
    labels: OrgChartPdfLabels,
    logoPortal: PdfImage,
    logoVv: PdfImage,
): PdfCanvasDocument {
    val pages = mutableListOf<DirectorPage>()
    val sortedDirectors = data.directors.filter { !it.inactive }.sortedBy { it.name.orgChartSortKey() }
    sortedDirectors.forEach { director ->
        pages +=
            DirectorPage(
                director = director,
                managers =
                    data.managersOfDirector(director.id).map { manager ->
                        ManagerBlock(manager, data.companiesOfManager(manager.id, director.id))
                    },
                directCompanies = data.directCompaniesOfDirector(director.id),
            )
    }
    val withoutDirectorManagers =
        data.managersWithoutDirector().map { manager ->
            ManagerBlock(manager, data.companiesOfManager(manager.id))
        }
    if (withoutDirectorManagers.isNotEmpty()) {
        pages += DirectorPage(director = null, managers = withoutDirectorManagers, directCompanies = emptyList())
    }
    if (pages.isEmpty()) {
        pages += DirectorPage(director = null, managers = emptyList(), directCompanies = emptyList())
    }

    val date = dateOf(version)
    val versionNumber = version?.number.orEmpty()
    val consolidated =
        data.companies
            .filter { !it.inactive }
            .distinctBy { it.id }
            .groupingBy { it.type }
            .eachCount()
    val canvasPages =
        pages.mapIndexed { index, page ->
            layoutPage(
                page = page,
                pageNumber = index + 1,
                pageCount = pages.size,
                labels = labels,
                consolidated = consolidated,
                versionNumber = versionNumber,
                date = date,
                logoPortal = logoPortal,
                logoVv = logoVv,
            )
        }
    return PdfCanvasDocument(pageSize = PdfPageSize.A4_LANDSCAPE, pages = canvasPages)
}

private fun layoutPage(
    page: DirectorPage,
    pageNumber: Int,
    pageCount: Int,
    labels: OrgChartPdfLabels,
    consolidated: Map<CompanyType, Int>,
    versionNumber: String,
    date: String,
    logoPortal: PdfImage,
    logoVv: PdfImage,
): PdfCanvasPage {
    if (useHorizontalUnits(page)) {
        return layoutHorizontalUnitsPage(
            page = page,
            pageNumber = pageNumber,
            pageCount = pageCount,
            labels = labels,
            consolidated = consolidated,
            versionNumber = versionNumber,
            date = date,
            logoPortal = logoPortal,
            logoVv = logoVv,
        )
    }
    val pageSize = PdfPageSize.A4_LANDSCAPE
    val contentWidth = pageSize.width - PAGE_MARGIN_X * 2f
    val contentHeight = CHART_BOTTOM - CONTENT_TOP
    val centerX = pageSize.width / 2f
    val counts = countsOf(page)
    val hasDirector = page.director != null
    val busDrop = if (hasDirector) BUS_DROP else 0f
    val spineGap = if (hasDirector) SPINE_GAP else 0f

    val blocks = mutableListOf<ColumnBlock>()
    if (page.directCompanies.isNotEmpty()) {
        blocks += ColumnBlock(managers = emptyList(), companies = page.directCompanies)
    }
    blocks += columnBlocks(page.managers)
    val columns =
        blocks.map { block ->
            val managersWidth =
                block.managers.fold(0f) { total, manager -> total + managerPillWidth(manager.name) } +
                    SHARED_MANAGER_GAP * (block.managers.size - 1).coerceAtLeast(0)
            val widestCompany = block.companies.maxOfOrNull { unitPillWidth(it.name) } ?: 0f
            val width = maxOf(managersWidth, widestCompany).coerceAtMost(contentWidth)
            val unitsHeight =
                block.companies.size * UNIT_HEIGHT + (block.companies.size - 1) * UNIT_GAP
            val height =
                when {
                    block.companies.isEmpty() -> MANAGER_HEIGHT
                    block.managers.isEmpty() -> CONNECTOR + unitsHeight
                    else -> MANAGER_HEIGHT + CONNECTOR + unitsHeight
                }
            ColumnLayout(block = block, width = width, height = height)
        }
    val rows = packRows(columns, contentWidth)

    val rowsHeight =
        rows.fold(0f) { total, row -> total + busDrop + row.height } +
            ROW_GAP * (rows.size - 1).coerceAtLeast(0)
    val totalHeight =
        (if (hasDirector) BLOCK_HEIGHT else 0f) +
            (if (rows.isEmpty()) 0f else spineGap + rowsHeight)
    val scale = (contentHeight / totalHeight.coerceAtLeast(1f)).coerceAtMost(1f)

    val blockHeight = if (hasDirector) BLOCK_HEIGHT * scale else 0f
    val blockBottom = CONTENT_TOP + blockHeight
    val managerStyle = if (hasDirector) MANAGER_STYLE else PRIMARY_MANAGER_STYLE

    val emitter = PageEmitter(pageSize, scale)
    emitter.header(title = labels.title.uppercase(), slogan = labels.slogan, logo = logoPortal)
    emitter.totalsTable(
        counts = counts,
        labels = labels.totals,
        x = pageSize.width - PAGE_MARGIN_X - totalsTableWidth(counts),
        top = TOTALS_TOP,
    )
    if (pageNumber == pageCount) {
        emitter.text(
            x = PAGE_MARGIN_X,
            top = CONSOLIDATED_TOP - 12f,
            value = "Total consolidado",
            size = 8f,
            bold = true,
            color = TEXT_COLOR,
        )
        emitter.totalsTable(
            counts = consolidated,
            labels = labels.totals,
            x = PAGE_MARGIN_X,
            top = CONSOLIDATED_TOP,
        )
    }
    emitter.legend(counts = counts, labels = labels.legend)
    emitter.footer(
        current = pageNumber,
        total = pageCount,
        versionNumber = versionNumber,
        date = date,
        rights = labels.rights,
        logo = logoVv,
    )

    if (rows.isNotEmpty()) {
        val busPositions = mutableListOf<Float>()
        var busY = blockBottom + spineGap * scale
        rows.forEach { row ->
            busPositions += busY
            busY += (busDrop + row.height + ROW_GAP) * scale
        }
        if (hasDirector) {
            emitter.line(centerX, blockBottom, centerX, busPositions.last(), CONNECTOR_COLOR, 0.9f * scale)
            rows.forEachIndexed { index, row ->
                val y = busPositions[index]
                val rowX = centerX - row.width * scale / 2f
                val dropCenters = mutableListOf<Float>()
                var x = rowX
                row.columns.forEach { column ->
                    if (column.block.managers.isEmpty()) {
                        dropCenters += x + column.width * scale / 2f
                    } else {
                        dropCenters += managerCenters(column, x, scale)
                    }
                    x += (column.width + COLUMN_GAP) * scale
                }
                emitter.line(dropCenters.first(), y, dropCenters.last(), y, CONNECTOR_COLOR, 0.9f * scale)
                dropCenters.forEach { dropCenter ->
                    emitter.line(dropCenter, y, dropCenter, y + busDrop * scale, CONNECTOR_COLOR, 0.9f * scale)
                }
            }
        }
        rows.forEachIndexed { index, row ->
            val y = busPositions[index]
            var x = centerX - row.width * scale / 2f
            row.columns.forEach { column ->
                emitColumn(emitter, column, x, y + busDrop * scale, managerStyle)
                x += (column.width + COLUMN_GAP) * scale
            }
        }
    }

    if (hasDirector) {
        val blockName = page.director.name
        val blockText = blockName.uppercase()
        val blockWidthBase =
            (PdfText.width(blockText, BLOCK_FONT, bold = true) + 48f)
                .coerceIn(170f, 420f)
                .coerceAtMost(contentWidth)
        val blockWidth = blockWidthBase * scale
        val blockX = centerX - blockWidth / 2f
        emitter.rect(
            x = blockX,
            top = CONTENT_TOP,
            width = blockWidth,
            height = blockHeight,
            radius = 10f * scale,
            fill = PRIMARY_COLOR,
        )
        emitter.pillText(
            x = blockX,
            top = CONTENT_TOP,
            width = blockWidth,
            height = blockHeight,
            value = blockText,
            size = BLOCK_FONT * scale,
            bold = true,
            maxLines = 1,
            color = SURFACE_COLOR,
        )
    }

    return emitter.toPage()
}

private fun useHorizontalUnits(page: DirectorPage): Boolean {
    if (page.director == null) return false
    val managers = page.managers
    return when (managers.size) {
        0 -> page.directCompanies.isNotEmpty()
        1 -> true
        2 ->
            managers[0].companies.map { it.id }.sorted() ==
                managers[1].companies.map { it.id }.sorted()
        else -> false
    }
}

private fun packUnitLines(
    companies: List<Company>,
    maxWidth: Float,
): List<UnitLine> {
    val lines = mutableListOf<UnitLine>()
    var current = mutableListOf<Company>()
    var width = 0f
    companies.forEach { company ->
        val pillWidth = unitPillWidth(company.name)
        val projected = if (current.isEmpty()) pillWidth else width + UNIT_HORIZONTAL_GAP + pillWidth
        if (current.isNotEmpty() && projected > maxWidth) {
            lines += UnitLine(current, width)
            current = mutableListOf()
            width = 0f
        }
        width = if (current.isEmpty()) pillWidth else width + UNIT_HORIZONTAL_GAP + pillWidth
        current += company
    }
    if (current.isNotEmpty()) lines += UnitLine(current, width)
    return lines
}

private fun unitLineCenters(
    line: UnitLine,
    centerX: Float,
    scale: Float,
): List<Float> {
    val lineWidth = line.width * scale
    var unitX = centerX - lineWidth / 2f
    val centers = mutableListOf<Float>()
    line.companies.forEach { company ->
        val unitWidth = unitPillWidth(company.name) * scale
        centers += unitX + unitWidth / 2f
        unitX += unitWidth + UNIT_HORIZONTAL_GAP * scale
    }
    return centers
}

private fun directGroupHeight(lines: List<UnitLine>): Float =
    if (lines.isEmpty()) {
        0f
    } else {
        CONNECTOR + UNIT_HEIGHT + (lines.size - 1) * (CONNECTOR + CONNECTOR + UNIT_HEIGHT)
    }

private fun managerGroupHeight(
    lines: List<UnitLine>,
    hasManagers: Boolean,
): Float {
    if (!hasManagers) return 0f
    var height = CONNECTOR + MANAGER_HEIGHT
    if (lines.isNotEmpty()) {
        height += CONNECTOR + CONNECTOR + UNIT_HEIGHT
        height += (lines.size - 1) * (CONNECTOR + CONNECTOR + UNIT_HEIGHT)
    }
    return height
}

private fun layoutHorizontalUnitsPage(
    page: DirectorPage,
    pageNumber: Int,
    pageCount: Int,
    labels: OrgChartPdfLabels,
    consolidated: Map<CompanyType, Int>,
    versionNumber: String,
    date: String,
    logoPortal: PdfImage,
    logoVv: PdfImage,
): PdfCanvasPage {
    val pageSize = PdfPageSize.A4_LANDSCAPE
    val contentWidth = pageSize.width - PAGE_MARGIN_X * 2f
    val contentHeight = CHART_BOTTOM - CONTENT_TOP
    val centerX = pageSize.width / 2f
    val counts = countsOf(page)
    val director = requireNotNull(page.director)

    val managers = page.managers.map { it.manager }
    val managerWidths = managers.map { managerPillWidth(it.name) }
    val managersWidth =
        managerWidths.fold(0f) { total, width -> total + width } +
            SHARED_MANAGER_GAP * (managerWidths.size - 1).coerceAtLeast(0)
    val managerCompanies =
        page.managers
            .firstOrNull()
            ?.companies
            .orEmpty()
    val directCompanies = page.directCompanies
    val directMinWidth = directCompanies.maxOfOrNull { unitPillWidth(it.name) } ?: 0f
    val managerAvailable =
        if (directCompanies.isNotEmpty()) {
            (contentWidth - COLUMN_GAP - directMinWidth).coerceAtLeast(managersWidth)
        } else {
            contentWidth
        }
    val managerLines = packUnitLines(managerCompanies, managerAvailable)
    val managerGroupWidth =
        maxOf(managersWidth, managerLines.maxOfOrNull { it.width } ?: 0f).coerceAtMost(contentWidth)
    val directAvailable =
        if (directCompanies.isNotEmpty()) {
            (contentWidth - managerGroupWidth - COLUMN_GAP).coerceAtLeast(directMinWidth)
        } else {
            0f
        }
    val directLines = packUnitLines(directCompanies, directAvailable)
    val directGroupWidth = directLines.maxOfOrNull { it.width } ?: 0f
    val hasDirect = directLines.isNotEmpty()
    val hasManagers = managers.isNotEmpty()
    val rowWidth =
        directGroupWidth + (if (hasDirect && hasManagers) COLUMN_GAP else 0f) + managerGroupWidth
    val rowHeight = maxOf(directGroupHeight(directLines), managerGroupHeight(managerLines, hasManagers))
    val totalHeight = BLOCK_HEIGHT + SPINE_GAP + rowHeight
    val scale = (contentHeight / totalHeight.coerceAtLeast(1f)).coerceAtMost(1f)

    val blockHeight = BLOCK_HEIGHT * scale
    val blockBottom = CONTENT_TOP + blockHeight
    val emitter = PageEmitter(pageSize, scale)

    emitter.header(title = labels.title.uppercase(), slogan = labels.slogan, logo = logoPortal)
    emitter.totalsTable(
        counts = counts,
        labels = labels.totals,
        x = pageSize.width - PAGE_MARGIN_X - totalsTableWidth(counts),
        top = TOTALS_TOP,
    )
    if (pageNumber == pageCount) {
        emitter.text(
            x = PAGE_MARGIN_X,
            top = CONSOLIDATED_TOP - 12f,
            value = "Total consolidado",
            size = 8f,
            bold = true,
            color = TEXT_COLOR,
        )
        emitter.totalsTable(
            counts = consolidated,
            labels = labels.totals,
            x = PAGE_MARGIN_X,
            top = CONSOLIDATED_TOP,
        )
    }
    emitter.legend(counts = counts, labels = labels.legend)
    emitter.footer(
        current = pageNumber,
        total = pageCount,
        versionNumber = versionNumber,
        date = date,
        rights = labels.rights,
        logo = logoVv,
    )

    val blockText = director.name.uppercase()
    val blockWidthBase =
        (PdfText.width(blockText, BLOCK_FONT, bold = true) + 48f)
            .coerceIn(170f, 420f)
            .coerceAtMost(contentWidth)
    val blockWidth = blockWidthBase * scale
    val blockX = centerX - blockWidth / 2f
    emitter.rect(
        x = blockX,
        top = CONTENT_TOP,
        width = blockWidth,
        height = blockHeight,
        radius = 10f * scale,
        fill = PRIMARY_COLOR,
    )
    emitter.pillText(
        x = blockX,
        top = CONTENT_TOP,
        width = blockWidth,
        height = blockHeight,
        value = blockText,
        size = BLOCK_FONT * scale,
        bold = true,
        maxLines = 1,
        color = SURFACE_COLOR,
    )

    if (rowWidth <= 0f) return emitter.toPage()

    val rowBusY = blockBottom + SPINE_GAP * scale
    val rowWidthScaled = rowWidth * scale
    val rowX = centerX - rowWidthScaled / 2f
    val directWidthScaled = directGroupWidth * scale
    val managerWidthScaled = managerGroupWidth * scale
    val gapScaled = if (hasDirect && hasManagers) COLUMN_GAP * scale else 0f
    val directCenter = rowX + directWidthScaled / 2f
    val managerCenter = rowX + directWidthScaled + gapScaled + managerWidthScaled / 2f

    val managerTop = rowBusY + CONNECTOR * scale
    val managerBottom = managerTop + MANAGER_HEIGHT * scale
    val managerRowWidth = managersWidth * scale
    var managerCursor = managerCenter - managerRowWidth / 2f
    val managerCenterXs =
        managerWidths.map { baseWidth ->
            val width = baseWidth * scale
            val center = managerCursor + width / 2f
            managerCursor += width + SHARED_MANAGER_GAP * scale
            center
        }

    val firstDirectLine = directLines.firstOrNull()
    val directFirstCenters = mutableListOf<Float>()
    if (firstDirectLine != null) {
        val lineWidth = firstDirectLine.width * scale
        var unitX = directCenter - lineWidth / 2f
        firstDirectLine.companies.forEach { company ->
            val unitWidth = unitPillWidth(company.name) * scale
            directFirstCenters += unitX + unitWidth / 2f
            unitX += unitWidth + UNIT_HORIZONTAL_GAP * scale
        }
    }
    val dropCenters = directFirstCenters + managerCenterXs
    val busStart = minOf(dropCenters.minOrNull() ?: centerX, centerX)
    val busEnd = maxOf(dropCenters.maxOrNull() ?: centerX, centerX)

    emitter.line(centerX, blockBottom, centerX, rowBusY, CONNECTOR_COLOR, 0.9f * scale)
    emitter.line(busStart, rowBusY, busEnd, rowBusY, CONNECTOR_COLOR, 0.9f * scale)

    val directPlacements = mutableListOf<PlacedUnit>()
    var directPreviousBottom = 0f
    directLines.forEachIndexed { index, line ->
        val lineWidth = line.width * scale
        val lineX = directCenter - lineWidth / 2f
        val lineBusY = if (index == 0) rowBusY else directPreviousBottom + CONNECTOR * scale
        val unitTop = lineBusY + CONNECTOR * scale
        if (index > 0) {
            emitter.line(directCenter, rowBusY, directCenter, lineBusY, CONNECTOR_COLOR, 0.9f * scale)
            val centers = unitLineCenters(line, directCenter, scale)
            if (centers.isNotEmpty()) {
                emitter.line(centers.first(), lineBusY, centers.last(), lineBusY, CONNECTOR_COLOR, 0.9f * scale)
            }
        }
        var unitX = lineX
        line.companies.forEach { company ->
            val unitWidth = unitPillWidth(company.name) * scale
            val unitCenter = unitX + unitWidth / 2f
            emitter.line(unitCenter, lineBusY, unitCenter, unitTop, CONNECTOR_COLOR, 0.9f * scale)
            directPlacements += PlacedUnit(company, unitX, unitTop, unitWidth)
            unitX += unitWidth + UNIT_HORIZONTAL_GAP * scale
        }
        directPreviousBottom = unitTop + UNIT_HEIGHT * scale
    }
    directPlacements.forEach { placed ->
        emitPill(emitter, placed.company.name, placed.company.type, placed.x, placed.top, placed.width)
    }

    if (hasManagers) {
        managerCenterXs.forEach { center ->
            emitter.line(center, rowBusY, center, managerTop, CONNECTOR_COLOR, 0.9f * scale)
        }

        val managerUnitPlacements = mutableListOf<PlacedUnit>()
        if (managerLines.isNotEmpty()) {
            val managerBusY = managerBottom + CONNECTOR * scale
            managerCenterXs.forEach { center ->
                emitter.line(center, managerBottom, center, managerBusY, CONNECTOR_COLOR, 0.9f * scale)
            }
            val firstCenters =
                managerLines.firstOrNull()?.let { unitLineCenters(it, managerCenter, scale) }.orEmpty()
            if (firstCenters.isNotEmpty()) {
                emitter.line(
                    firstCenters.first(),
                    managerBusY,
                    firstCenters.last(),
                    managerBusY,
                    CONNECTOR_COLOR,
                    0.9f * scale,
                )
            }
            var managerPreviousBottom = managerBusY
            managerLines.forEachIndexed { index, line ->
                val lineWidth = line.width * scale
                val lineX = managerCenter - lineWidth / 2f
                val lineBusY = if (index == 0) managerBusY else managerPreviousBottom + CONNECTOR * scale
                val unitTop = lineBusY + CONNECTOR * scale
                if (index > 0) {
                    emitter.line(managerCenter, managerBusY, managerCenter, lineBusY, CONNECTOR_COLOR, 0.9f * scale)
                    val centers = unitLineCenters(line, managerCenter, scale)
                    if (centers.isNotEmpty()) {
                        emitter.line(centers.first(), lineBusY, centers.last(), lineBusY, CONNECTOR_COLOR, 0.9f * scale)
                    }
                }
                var unitX = lineX
                line.companies.forEach { company ->
                    val unitWidth = unitPillWidth(company.name) * scale
                    val unitCenter = unitX + unitWidth / 2f
                    emitter.line(unitCenter, lineBusY, unitCenter, unitTop, CONNECTOR_COLOR, 0.9f * scale)
                    managerUnitPlacements += PlacedUnit(company, unitX, unitTop, unitWidth)
                    unitX += unitWidth + UNIT_HORIZONTAL_GAP * scale
                }
                managerPreviousBottom = unitTop + UNIT_HEIGHT * scale
            }
        }

        var pillX = managerCenter - managerRowWidth / 2f
        managers.forEachIndexed { index, manager ->
            val managerWidth = managerWidths[index] * scale
            emitter.rect(
                x = pillX,
                top = managerTop,
                width = managerWidth,
                height = MANAGER_HEIGHT * scale,
                radius = MANAGER_HEIGHT * scale / 2f,
                fill = MANAGER_STYLE.fill,
                stroke = MANAGER_STYLE.border,
                strokeWidth = 0.7f * scale,
            )
            emitter.pillText(
                x = pillX,
                top = managerTop,
                width = managerWidth,
                height = MANAGER_HEIGHT * scale,
                value = manager.name.uppercase(),
                size = MANAGER_FONT * scale,
                bold = true,
                maxLines = 1,
                color = MANAGER_STYLE.text,
            )
            pillX += managerWidth + SHARED_MANAGER_GAP * scale
        }
        managerUnitPlacements.forEach { placed ->
            emitPill(emitter, placed.company.name, placed.company.type, placed.x, placed.top, placed.width)
        }
    }

    return emitter.toPage()
}

private fun managerCenters(
    column: ColumnLayout,
    columnX: Float,
    scale: Float,
): List<Float> {
    val widths = column.block.managers.map { managerPillWidth(it.name) * scale }
    val total =
        widths.fold(0f) { acc, width -> acc + width } +
            SHARED_MANAGER_GAP * (widths.size - 1).coerceAtLeast(0) * scale
    var x = columnX + (column.width * scale - total) / 2f
    return widths.map { width ->
        val center = x + width / 2f
        x += width + SHARED_MANAGER_GAP * scale
        center
    }
}

private fun emitColumn(
    emitter: PageEmitter,
    column: ColumnLayout,
    x: Float,
    top: Float,
    managerStyle: PillStyle,
) {
    val scale = emitter.scale
    val columnWidth = column.width * scale
    val center = x + columnWidth / 2f
    val managerHeight = MANAGER_HEIGHT * scale
    val managerWidths = column.block.managers.map { managerPillWidth(it.name) * scale }
    val managersWidth =
        managerWidths.fold(0f) { total, width -> total + width } +
            SHARED_MANAGER_GAP * (managerWidths.size - 1).coerceAtLeast(0) * scale
    var managerX = center - managersWidth / 2f
    column.block.managers.forEachIndexed { index, manager ->
        val managerWidth = managerWidths[index]
        emitter.rect(
            x = managerX,
            top = top,
            width = managerWidth,
            height = managerHeight,
            radius = managerHeight / 2f,
            fill = managerStyle.fill,
            stroke = managerStyle.border,
            strokeWidth = 0.7f * scale,
        )
        val managerName = manager.name.uppercase()
        emitter.pillText(
            x = managerX,
            top = top,
            width = managerWidth,
            height = managerHeight,
            value = managerName,
            size = MANAGER_FONT * scale,
            bold = true,
            maxLines = 1,
            color = managerStyle.text,
        )
        managerX += managerWidth + SHARED_MANAGER_GAP * scale
    }
    var unitTop = top + managerHeight
    if (column.block.companies.isEmpty()) return
    when {
        managerWidths.isEmpty() -> {
            unitTop = top
            emitter.line(center, top, center, top + CONNECTOR * scale, CONNECTOR_COLOR, 0.9f * scale)
        }
        managerWidths.size > 1 -> {
            val centers = managerCenters(column, x, scale)
            val busY = unitTop + CONNECTOR * scale / 2f
            emitter.line(centers.first(), busY, centers.last(), busY, CONNECTOR_COLOR, 0.9f * scale)
            centers.forEach { managerCenter ->
                emitter.line(managerCenter, unitTop, managerCenter, busY, CONNECTOR_COLOR, 0.9f * scale)
            }
            emitter.line(center, busY, center, unitTop + CONNECTOR * scale, CONNECTOR_COLOR, 0.9f * scale)
        }
        else -> {
            emitter.line(center, unitTop, center, unitTop + CONNECTOR * scale, CONNECTOR_COLOR, 0.9f * scale)
        }
    }
    unitTop += CONNECTOR * scale
    column.block.companies.forEachIndexed { index, company ->
        val unitWidth = unitPillWidth(company.name) * scale
        emitPill(
            emitter = emitter,
            name = company.name,
            type = company.type,
            x = center - unitWidth / 2f,
            top = unitTop,
            width = unitWidth,
        )
        if (index < column.block.companies.lastIndex) {
            emitter.line(
                center,
                unitTop + UNIT_HEIGHT * scale,
                center,
                unitTop + (UNIT_HEIGHT + UNIT_GAP) * scale,
                CONNECTOR_COLOR,
                0.9f * scale,
            )
        }
        unitTop += (UNIT_HEIGHT + UNIT_GAP) * scale
    }
}

private fun emitPill(
    emitter: PageEmitter,
    name: String,
    type: CompanyType,
    x: Float,
    top: Float,
    width: Float,
) {
    val scale = emitter.scale
    val height = UNIT_HEIGHT * scale
    val style = type.pillStyle()
    emitter.rect(
        x = x,
        top = top,
        width = width,
        height = height,
        radius = height / 2f,
        fill = style.fill,
        stroke = style.border,
        strokeWidth = 0.7f * scale,
    )
    emitter.pillText(
        x = x,
        top = top,
        width = width,
        height = height,
        value = name.uppercase(),
        size = UNIT_FONT * scale,
        bold = true,
        maxLines = 2,
        color = style.text,
    )
}

private fun columnBlocks(managers: List<ManagerBlock>): List<ColumnBlock> =
    groupByCompanySet(managers) { it.companies }.map { group ->
        ColumnBlock(group.map { it.manager }, group.first().companies)
    }

private fun packRows(
    columns: List<ColumnLayout>,
    maxWidth: Float,
): List<RowLayout> {
    val rows = mutableListOf<RowLayout>()
    var current = mutableListOf<ColumnLayout>()
    var width = 0f
    columns.forEach { column ->
        val projected = if (current.isEmpty()) column.width else width + COLUMN_GAP + column.width
        if (current.isNotEmpty() && projected > maxWidth) {
            rows += RowLayout(columns = current, width = width, height = current.maxOf { it.height })
            current = mutableListOf()
            width = 0f
        }
        width = if (current.isEmpty()) column.width else width + COLUMN_GAP + column.width
        current += column
    }
    if (current.isNotEmpty()) {
        rows += RowLayout(columns = current, width = width, height = current.maxOf { it.height })
    }
    return rows
}

private fun managerPillWidth(name: String): Float = maxOf(60f, PdfText.width(name.uppercase(), MANAGER_FONT, bold = true) + 18f)

private fun unitPillWidth(name: String): Float = maxOf(56f, PdfText.width(name.uppercase(), UNIT_FONT, bold = true) + 16f)

private fun countsOf(page: DirectorPage): Map<CompanyType, Int> =
    (page.managers.flatMap { it.companies } + page.directCompanies)
        .distinctBy { it.id }
        .groupingBy { it.type }
        .eachCount()

private fun totalsTableWidth(counts: Map<CompanyType, Int>): Float {
    val types = CompanyType.entries.count { (counts[it] ?: 0) > 0 }
    if (types == 0) return 0f
    return types * TOTAL_CELL_WIDTH + (types - 1) * TOTAL_CELL_GAP
}

private fun CompanyType.pillStyle(): PillStyle =
    when (this) {
        CompanyType.UPR -> PillStyle(PdfColor(255, 243, 205), PdfColor(245, 158, 11), PdfColor(102, 77, 3))
        CompanyType.TRA -> PillStyle(PdfColor(209, 231, 221), PdfColor(46, 125, 50), PdfColor(15, 81, 50))
        CompanyType.LOJ -> PillStyle(PdfColor(248, 215, 218), PdfColor(220, 53, 69), PdfColor(114, 28, 36))
        CompanyType.EVENTOS -> PillStyle(PdfColor(207, 244, 252), PdfColor(13, 202, 240), PdfColor(5, 81, 96))
    }

private fun dateOf(version: OrgChartVersion?): String =
    listOfNotNull(version?.publishedAt, version?.editedAt, version?.effectiveFrom)
        .firstOrNull { it.isNotBlank() }
        ?.substringBefore(" às ")
        .orEmpty()

private val YEAR_PATTERN = Regex("(19|20)\\d{2}")

private fun yearOf(date: String): String = YEAR_PATTERN.find(date)?.value.orEmpty()

private class PageEmitter(
    private val pageSize: PdfPageSize,
    val scale: Float,
) {
    private val ops = mutableListOf<PdfOp>()

    fun toPage(): PdfCanvasPage = PdfCanvasPage(ops = ops.toList())

    fun rect(
        x: Float,
        top: Float,
        width: Float,
        height: Float,
        radius: Float,
        fill: PdfColor?,
        stroke: PdfColor? = null,
        strokeWidth: Float = 1f,
    ) {
        ops +=
            PdfOp.Rect(
                x = x,
                y = pageSize.height - top - height,
                width = width,
                height = height,
                radius = radius,
                fill = fill,
                stroke = stroke,
                strokeWidth = strokeWidth,
            )
    }

    fun line(
        x1: Float,
        top1: Float,
        x2: Float,
        top2: Float,
        color: PdfColor,
        width: Float,
    ) {
        ops +=
            PdfOp.Line(
                x1 = x1,
                y1 = pageSize.height - top1,
                x2 = x2,
                y2 = pageSize.height - top2,
                color = color,
                width = width,
            )
    }

    fun text(
        x: Float,
        top: Float,
        value: String,
        size: Float,
        bold: Boolean = false,
        color: PdfColor = TEXT_COLOR,
        align: Align = Align.Left,
        width: Float = 0f,
    ) {
        val measured = PdfText.width(value, size, bold)
        val drawX =
            when (align) {
                Align.Left -> x
                Align.Center -> x + (width - measured) / 2f
                Align.Right -> x + width - measured
            }
        ops +=
            PdfOp.Text(
                x = drawX,
                y = pageSize.height - top - size * 0.8f,
                text = value,
                size = size,
                bold = bold,
                color = color,
            )
    }

    fun pillText(
        x: Float,
        top: Float,
        width: Float,
        height: Float,
        value: String,
        size: Float,
        bold: Boolean = false,
        maxLines: Int,
        color: PdfColor,
    ) {
        val lines = PdfText.wrap(value, width - 8f * scale, size, bold, maxLines)
        if (lines.isEmpty()) return
        val lineHeight = size * 1.15f
        val textTop = top + (height - lineHeight * lines.size) / 2f
        lines.forEachIndexed { index, line ->
            text(
                x = x,
                top = textTop + index * lineHeight,
                value = line,
                size = size,
                bold = bold,
                color = color,
                align = Align.Center,
                width = width,
            )
        }
    }

    fun image(
        image: PdfImage,
        x: Float,
        top: Float,
        width: Float,
        height: Float,
    ) {
        ops +=
            PdfOp.Image(
                image = image,
                x = x,
                y = pageSize.height - top - height,
                width = width,
                height = height,
            )
    }

    fun header(
        title: String,
        slogan: String,
        logo: PdfImage,
    ) {
        val logoHeight = 26f
        val logoWidth = logoHeight * logo.width.toFloat() / logo.height
        image(logo, x = PAGE_MARGIN_X, top = HEADER_LOGO_TOP, width = logoWidth, height = logoHeight)
        if (slogan.isNotBlank()) {
            text(
                x = PAGE_MARGIN_X,
                top = SLOGAN_TOP,
                value = slogan,
                size = 6.5f,
                color = MUTED_COLOR,
            )
        }
        text(
            x = 0f,
            top = TITLE_TOP,
            value = title,
            size = 20f,
            bold = true,
            color = TEXT_COLOR,
            align = Align.Center,
            width = pageSize.width,
        )
    }

    fun totalsTable(
        counts: Map<CompanyType, Int>,
        labels: Map<CompanyType, String>,
        x: Float,
        top: Float,
    ) {
        val types = CompanyType.entries.filter { (counts[it] ?: 0) > 0 }
        if (types.isEmpty()) return
        var columnX = x
        types.forEach { type ->
            rect(columnX, top, TOTAL_CELL_WIDTH, TOTAL_CELL_HEIGHT, 0f, fill = TOTAL_HEADER_FILL)
            text(
                x = columnX,
                top = top + TOTAL_CELL_HEIGHT / 2f - 3.5f,
                value = labels[type] ?: type.name,
                size = 7f,
                bold = true,
                color = TEXT_COLOR,
                align = Align.Center,
                width = TOTAL_CELL_WIDTH,
            )
            val valueTop = top + TOTAL_CELL_HEIGHT + TOTAL_CELL_GAP
            rect(columnX, valueTop, TOTAL_CELL_WIDTH, TOTAL_CELL_HEIGHT, 0f, fill = TOTAL_VALUE_FILL)
            text(
                x = columnX,
                top = valueTop + TOTAL_CELL_HEIGHT / 2f - 4f,
                value = (counts[type] ?: 0).toString().padStart(2, '0'),
                size = 8f,
                bold = true,
                color = TEXT_COLOR,
                align = Align.Center,
                width = TOTAL_CELL_WIDTH,
            )
            columnX += TOTAL_CELL_WIDTH + TOTAL_CELL_GAP
        }
    }

    fun legend(
        counts: Map<CompanyType, Int>,
        labels: Map<CompanyType, String>,
    ) {
        val types = CompanyType.entries.filter { (counts[it] ?: 0) > 0 }
        if (types.isEmpty()) return
        val boxWidth = 150f
        val rowHeight = 12f
        val padding = 6f
        val boxHeight = types.size * rowHeight + padding * 2f
        val boxX = pageSize.width - PAGE_MARGIN_X - boxWidth
        val boxTop = LEGEND_BOTTOM - boxHeight
        text(
            x = boxX,
            top = boxTop - 12f,
            value = "Legenda:",
            size = 8f,
            bold = true,
            color = TEXT_COLOR,
        )
        rect(boxX, boxTop, boxWidth, boxHeight, radius = 12f, fill = LEGEND_FILL)
        var y = boxTop + padding
        types.forEach { type ->
            val style = type.pillStyle()
            val rowCenter = y + rowHeight / 2f
            val dotSize = 9f
            val labelSize = 7.5f
            rect(
                x = boxX + 12f,
                top = rowCenter - dotSize / 2f,
                width = dotSize,
                height = dotSize,
                radius = dotSize / 2f,
                fill = style.fill,
                stroke = style.border,
                strokeWidth = 0.6f,
            )
            text(
                x = boxX + 26f,
                top = rowCenter - labelSize * 0.55f,
                value = labels[type] ?: type.name,
                size = labelSize,
                color = TEXT_COLOR,
            )
            y += rowHeight
        }
    }

    fun footer(
        current: Int,
        total: Int,
        versionNumber: String,
        date: String,
        rights: String,
        logo: PdfImage,
    ) {
        val logoHeight = 14f
        val logoWidth = logoHeight * logo.width.toFloat() / logo.height
        image(logo, x = PAGE_MARGIN_X, top = FOOTER_LOGO_TOP, width = logoWidth, height = logoHeight)
        val year = yearOf(date)
        val period = if (rights.endsWith(".")) "" else "."
        val copyright = if (year.isBlank()) "© $rights$period" else "© $year. $rights$period"
        text(
            x = PAGE_MARGIN_X,
            top = FOOTER_RIGHTS_TOP,
            value = copyright,
            size = 6.5f,
            color = MUTED_COLOR,
        )
        val center =
            if (versionNumber.isBlank()) {
                "Página $current de $total"
            } else {
                "$versionNumber - Página $current de $total"
            }
        text(
            x = PAGE_MARGIN_X,
            top = FOOTER_TEXT_TOP,
            value = center,
            size = 7f,
            color = MUTED_COLOR,
            align = Align.Center,
            width = pageSize.width - PAGE_MARGIN_X * 2f,
        )
        if (date.isNotBlank()) {
            text(
                x = pageSize.width - PAGE_MARGIN_X,
                top = FOOTER_TEXT_TOP,
                value = "Atualização: $date",
                size = 7.5f,
                bold = true,
                color = TEXT_COLOR,
                align = Align.Right,
            )
        }
    }
}
